import { Component, OnInit, OnDestroy, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatAngularModule } from '../../modules/mat-angular.module';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router, ActivatedRoute } from '@angular/router';
import { GenericTemplateCommonService } from '../../components/template-ai-common/services/generic-template-common.service';
import { BulkSearchService } from '../../../services/bulk-search.service';
import { GlobalLoaderService } from '../../global-loader/global-loader.service';
import { GenericCommanService } from '../../../services/generic-common.service';
import { ProjectHistoryService } from '../../../services/project-history.service';
import { TaskProgressWebSocketService, WsProgressMessage } from '../../../services/task-progress-websocket.service';
import { merge, Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { CompositeScoreWidgetComponent } from '../../components/composite-score-widget/composite-score-widget.component';
import { BarLineComboChartComponent } from '../../charts/bar-line-combo-chart/bar-line-combo-chart.component';
import { AreaChartComponent } from '../../charts/area-chart/area-chart.component';
import { CommonCircleLoaderComponent } from '../../components/common-circle-loader/common-circle-loader.component';
// import { CitationCardComponent } from '../../components/citation-card/citation-card.component';
import { TaskStatus } from '../../models/bulk-search.models';

@Component({
  selector: 'app-generic-eeat-audit',
  standalone: true,
  imports: [
    CommonModule,
    MatAngularModule,
    CompositeScoreWidgetComponent,
    BarLineComboChartComponent,
    AreaChartComponent,
    CommonCircleLoaderComponent,
    // CitationCardComponent
  ],
  templateUrl: './generic-eeat-audit.component.html',
  styleUrl: './generic-eeat-audit.component.scss'
})
export class GenericEeatAuditComponent implements OnInit, OnDestroy {
  auditResult: any = null;
  auditError: string | null = null;
  loading: boolean = false;
  private subscriptions: Subscription[] = [];

  // New properties for enhanced display
  compositeScore: number = 0;
  dimensionScores: {
    Experience: { current: number; potential: number };
    Expertise: { current: number; potential: number };
    Authoritativeness: { current: number; potential: number };
    Trustworthiness: { current: number; potential: number };
  } = {
    Experience: { current: 0, potential: 0 },
    Expertise: { current: 0, potential: 0 },
    Authoritativeness: { current: 0, potential: 0 },
    Trustworthiness: { current: 0, potential: 0 }
  };
  questionsData: Array<{
    dimension: string;
    question: string;
    pass: number;
    recommendation: string | null;
  }> = [];
  
  // Chart data for reusable components
  horizontalBarChartData: any[] = []; // Format: [{name: "Dimension", value: score}]
  radarChartData: any[] = []; // Format: [{name: "Current/Potential", series: [{name: "Dimension", value: score}]}]

  // Evidence sources
  evidenceSources: any[] = [];

  private snackBar = inject(MatSnackBar);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private bulkSearchService = inject(BulkSearchService);
  private globalLoaderService = inject(GlobalLoaderService);
  private genericCommanService = inject(GenericCommanService);
  private projectHistoryService = inject(ProjectHistoryService);
  private taskProgressWs = inject(TaskProgressWebSocketService);
  reAuditLoading = false;
  private projectId: string | null = null;
  private statusPollingInterval: any = null;
  private wsSubscription: Subscription | undefined;
  /** Same task also emits on path "analysis" when DB row completes — dismiss loader if audit WS missed. */
  private analysisWsSub: Subscription | undefined;
  /** Avoid double completion when multiple WS paths deliver the same terminal event. */
  private eeatAuditTerminalHandled = false;
  
  constructor(
    private genericTemplateCommonService: GenericTemplateCommonService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    // Get projectId from route params
    this.route.params.subscribe(params => {
      this.projectId = params['projectId'] || null;
    });
    
    // Subscribe to EEAT audit response from service
    const eeatAuditSub = this.genericTemplateCommonService.eeatAuditResponse$.subscribe((response: any) => {
      if (response) {
        this.auditResult = response;
        this.processEeatAuditData(response);
        this.loading = false;
        this.auditError = null;
        this.cdr.detectChanges();
      }
    });

    // Subscribe to loading state
    const loadingSub = this.genericTemplateCommonService.isLoading$.subscribe(loading => {
      this.loading = loading;
    });

    // Subscribe to error state
    const errorSub = this.genericTemplateCommonService.error$.subscribe(error => {
      if (error) {
        this.auditError = error;
      }
    });

    // Add subscriptions to array
    this.subscriptions.push(eeatAuditSub, loadingSub, errorSub);

    // Check if we already have EEAT audit data from history or previous analysis
    const existingData = this.genericTemplateCommonService.getCurrentEeatAuditResponse();
    if (existingData) {
      this.auditResult = existingData;
      this.processEeatAuditData(existingData);
      this.loading = false;
      this.cdr.detectChanges();
    } else {
      // If no existing data, try to load from ProjectData history API (only on page load)
      const urlParams = new URLSearchParams(window.location.search);
      const projectdataId = urlParams.get('projectdataId');
      if (projectdataId) {
        this.loadAuditDataFromHistory(projectdataId);
      }
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.wsSubscription?.unsubscribe();
    this.analysisWsSub?.unsubscribe();
    if (this.statusPollingInterval) {
      clearInterval(this.statusPollingInterval);
    }
  }

  private stopStatusPolling(): void {
    if (this.statusPollingInterval) {
      clearInterval(this.statusPollingInterval);
      this.statusPollingInterval = null;
    }
    this.wsSubscription?.unsubscribe();
    this.wsSubscription = undefined;
    this.analysisWsSub?.unsubscribe();
    this.analysisWsSub = undefined;
  }

  /**
   * Start task-based audit with status polling
   */
  private startTaskBasedAudit(request: { project_id?: string; projectdata_id?: string; filters?: any }): void {
    this.eeatAuditTerminalHandled = false;
    this.reAuditLoading = true;
    this.loading = true;
    this.auditError = null;
    
    // One UI "tab" for this re-audit; backend still reports % via WebSocket (internal steps hidden).
    this.globalLoaderService.initializeProgress(1);
    this.globalLoaderService.updateProgress(0, 0, [], 'Starting EEAT audit...', null, 1);
    
    // Start audit task
    this.genericCommanService.processEeatAudit(request).subscribe({
      next: (response: any) => {
        const taskId = response.task_id;
        if (!taskId) {
          this.auditError = 'Failed to start audit: No task ID returned';
          this.reAuditLoading = false;
          this.loading = false;
          return;
        }
        
        // Start polling status
        this.startStatusPolling(taskId);
      },
      error: (error: any) => {
        console.error('Error starting audit:', error);
        this.auditError = error.error?.detail || 'Failed to start audit';
        this.reAuditLoading = false;
        this.loading = false;
        this.globalLoaderService.resetLoader();
      }
    });
  }

  /**
   * Poll audit task status (or subscribe to WebSocket when environment.useWebSocket).
   * Polling code kept for rollback – do not remove.
   */
  private startStatusPolling(taskId: string): void {
    const applyStatus = (status: any) => {
      const progressPercentage = status.progress_percentage || 0;
      const rawStep = status.current_step || 'Processing...';
      const currentStep =
        String(rawStep).toLowerCase().includes('eeat')
          ? rawStep
          : `eeat-audit — ${rawStep}`;
      const estimatedTime = status.estimated_time_remaining;
      this.globalLoaderService.updateProgress(
        progressPercentage,
        0,
        [],
        currentStep,
        estimatedTime,
        1
      );
    };
    const onCompleted = (status: any) => {
      if (status.result) {
        this.auditResult = status.result;
        this.processEeatAuditData(status.result);
        this.loading = false;
        this.reAuditLoading = false;
        this.globalLoaderService.resetLoader();
        this.genericTemplateCommonService.updateEeatAuditResponse(status.result);
        this.snackBar.open('EEAT audit completed! Results are shown below.', 'Close', { duration: 5000 });
      } else {
        this.auditError = 'Audit completed but no result available';
        this.loading = false;
        this.reAuditLoading = false;
        this.globalLoaderService.resetLoader();
      }
      this.cdr.detectChanges();
    };
    const onFailed = (status: any) => {
      this.auditError = status.error_message || 'Audit failed';
      this.loading = false;
      this.reAuditLoading = false;
      this.globalLoaderService.resetLoader();
      this.cdr.detectChanges();
    };

    if (this.taskProgressWs.useWebSocket) {
      this.stopStatusPolling();
      this.taskProgressWs.connect();
      const { projectId: pid, projectdataId: pdid } = this.genericTemplateCommonService.getCurrentProjectContext();
      const auditStreams: Observable<WsProgressMessage>[] = [
        this.taskProgressWs.messagesByPath('audit'),
      ];
      if (pid && pdid) {
        auditStreams.unshift(this.taskProgressWs.messagesByPath(`${pid}_${pdid}`));
      }
      this.wsSubscription = merge(...auditStreams)
        .pipe(filter((msg) => (msg.data as any)?.task_id === taskId))
        .subscribe((msg) => {
          const status = msg.data as any;
          if (!status) return;
          applyStatus(status);
          if (status.status === 'completed') {
            if (this.eeatAuditTerminalHandled) return;
            this.eeatAuditTerminalHandled = true;
            this.stopStatusPolling();
            onCompleted(status);
          } else if (status.status === 'failed' || status.status === 'cancelled') {
            if (this.eeatAuditTerminalHandled) return;
            this.eeatAuditTerminalHandled = true;
            this.stopStatusPolling();
            if (status.status === 'cancelled') {
              this.auditError = 'Audit was cancelled';
              this.loading = false;
              this.reAuditLoading = false;
              this.globalLoaderService.resetLoader();
              this.cdr.detectChanges();
            } else {
              onFailed(status);
            }
          }
          this.cdr.detectChanges();
        });

      // DB analysis row completes on path "analysis" (always) — fallback if audit Redis/WS path mismatch.
      this.analysisWsSub = this.taskProgressWs
        .messagesByPath('analysis')
        .pipe(
          filter((msg) => {
            const d = msg.data as Record<string, unknown> | undefined;
            return (
              d?.['task_id'] === taskId &&
              ['completed', 'failed', 'cancelled'].includes(String(d?.['status'] || ''))
            );
          })
        )
        .subscribe((msg) => {
          if (this.eeatAuditTerminalHandled) return;
          const d = msg.data as Record<string, unknown>;
          const st = String(d['status'] || '');
          this.eeatAuditTerminalHandled = true;
          this.stopStatusPolling();
          if (st === 'completed') {
            this.globalLoaderService.resetLoader();
            this.reAuditLoading = false;
            this.loading = false;
            const pd =
              pdid ||
              this.genericTemplateCommonService.getCurrentProjectContext().projectdataId ||
              new URLSearchParams(window.location.search).get('projectdataId');
            if (pd) {
              this.loadAuditDataFromHistory(pd);
              this.snackBar.open('EEAT audit completed! Refreshing results…', 'Close', { duration: 5000 });
            } else {
              this.snackBar.open('EEAT audit completed.', 'Close', { duration: 5000 });
            }
          } else if (st === 'cancelled') {
            this.auditError = 'Audit was cancelled';
            this.loading = false;
            this.reAuditLoading = false;
            this.globalLoaderService.resetLoader();
          } else {
            onFailed({ error_message: 'Audit failed' });
          }
          this.cdr.detectChanges();
        });
      return;
    }

    // Fallback: polling
    let pollCount = 0;
    const initialInterval = 2000;
    const regularInterval = 5000;
    const pollStatus = () => {
      this.genericCommanService.getAuditStatus(taskId).subscribe({
        next: (status: any) => {
          pollCount++;
          applyStatus(status);
          if (status.status === 'completed') {
            if (this.statusPollingInterval) {
              clearInterval(this.statusPollingInterval);
              this.statusPollingInterval = null;
            }
            onCompleted(status);
          } else if (status.status === 'failed' || status.status === 'cancelled') {
            if (this.statusPollingInterval) {
              clearInterval(this.statusPollingInterval);
              this.statusPollingInterval = null;
            }
            if (status.status === 'cancelled') {
              this.auditError = 'Audit was cancelled';
              this.loading = false;
              this.reAuditLoading = false;
              this.globalLoaderService.resetLoader();
            } else {
              onFailed(status);
            }
          } else {
            if (pollCount === 3 && this.statusPollingInterval) {
              clearInterval(this.statusPollingInterval);
              this.statusPollingInterval = setInterval(pollStatus, regularInterval);
            }
          }
          this.cdr.detectChanges();
        },
        error: (error: any) => {
          console.error('Error polling status:', error);
          if (this.statusPollingInterval) {
            clearInterval(this.statusPollingInterval);
            this.statusPollingInterval = null;
          }
          this.auditError = 'Failed to get audit status';
          this.loading = false;
          this.reAuditLoading = false;
          this.globalLoaderService.resetLoader();
          this.cdr.detectChanges();
        }
      });
    };
    this.statusPollingInterval = setInterval(pollStatus, initialInterval);
    pollStatus();
  }

  /**
   * Process EEAT audit data and populate component properties
   */
  processEeatAuditData(data: any): void {
    // Handle both old and new data structures
    // New structure: { composite_score, brands: [{ brand, dimensions: {...} }] }
    // Old structure: { brands: [{ brand, audit: [...] }] }
    
    if (!data) return;

    // Extract data from structured_data if present (from bulk search)
    const actualData = data.structured_data || data;

    // Extract evidence sources from Tavily
    this.evidenceSources = this.transformSourcesData(
      actualData.evidence_sources || 
      data.evidence_sources || 
      []
    );

    // Get your brand result
    const yourBrand = this.getYourBrandAuditResult();
    if (!yourBrand) return;

    // Get composite score (prefer brand-specific, fallback to overall)
    this.compositeScore = yourBrand.composite_score || actualData.composite_score || 0;

    // Process dimensions (new structure)
    if (yourBrand.dimensions) {
      const dimensions = yourBrand.dimensions;
      
      // Extract dimension scores
      this.dimensionScores = {
        Experience: {
          current: dimensions.Experience?.score || 0,
          potential: dimensions.Experience?.potential_score || 0
        },
        Expertise: {
          current: dimensions.Expertise?.score || 0,
          potential: dimensions.Expertise?.potential_score || 0
        },
        Authoritativeness: {
          current: dimensions.Authoritativeness?.score || 0,
          potential: dimensions.Authoritativeness?.potential_score || 0
        },
        Trustworthiness: {
          current: dimensions.Trustworthiness?.score || 0,
          potential: dimensions.Trustworthiness?.potential_score || 0
        }
      };

      // Collect all questions
      this.questionsData = [];
      Object.keys(dimensions).forEach(dim => {
        const dimData = dimensions[dim];
        if (dimData.questions && Array.isArray(dimData.questions)) {
          dimData.questions.forEach((q: any) => {
            this.questionsData.push({
              dimension: dim,
              question: q.question || '',
              pass: q.pass || 0,
              recommendation: q.pass === 0 ? (dimData.recommendations?.[0] || null) : null
            });
          });
        }
      });
    } else if (yourBrand.audit) {
      // Old structure - convert to new format
      yourBrand.audit.forEach((auditItem: any) => {
        this.dimensionScores[auditItem.dimension as keyof typeof this.dimensionScores] = {
          current: auditItem.score || 0,
          potential: auditItem.score || 0
        };
      });
    }

    // Update chart data for reusable components
    this.updateHorizontalBarChart();
    this.updateRadarChart();
  }

  /**
   * Update horizontal bar chart data for bar-line-combo-chart component
   * Format: [{name: "Dimension", value: score}]
   */
  updateHorizontalBarChart(): void {
    this.horizontalBarChartData = [
      {
        name: 'Experience',
        value: this.dimensionScores.Experience.current
      },
      {
        name: 'Expertise',
        value: this.dimensionScores.Expertise.current
      },
      {
        name: 'Authoritativeness',
        value: this.dimensionScores.Authoritativeness.current
      },
      {
        name: 'Trustworthiness',
        value: this.dimensionScores.Trustworthiness.current
      }
    ];
  }

  /**
   * Update radar chart data for area-chart component
   * Format: [{name: "Current/Potential", series: [{name: "Dimension", value: score}]}]
   */
  updateRadarChart(): void {
    this.radarChartData = [
      {
        name: 'Current',
        series: [
          {
            name: 'Experience',
            value: this.dimensionScores.Experience.current
          },
          {
            name: 'Expertise',
            value: this.dimensionScores.Expertise.current
          },
          {
            name: 'Authoritativeness',
            value: this.dimensionScores.Authoritativeness.current
          },
          {
            name: 'Trustworthiness',
            value: this.dimensionScores.Trustworthiness.current
          }
        ]
      },
      {
        name: 'Potential',
        series: [
          {
            name: 'Experience',
            value: this.dimensionScores.Experience.potential
          },
          {
            name: 'Expertise',
            value: this.dimensionScores.Expertise.potential
          },
          {
            name: 'Authoritativeness',
            value: this.dimensionScores.Authoritativeness.potential
          },
          {
            name: 'Trustworthiness',
            value: this.dimensionScores.Trustworthiness.potential
          }
        ]
      }
    ];
  }

  get groupedBarData() {
    if (!this.auditResult || !this.auditResult.brands) return [];
    // Each brand is a group, each dimension is a series
    return this.auditResult.brands.map((brand: any) => ({
      name: brand.brand,
      series: brand.audit ? brand.audit.map((a: any) => ({
        name: a.dimension,
        value: a.score
      })) : (brand.dimensions ? Object.keys(brand.dimensions).map((dim: string) => ({
        name: dim,
        value: brand.dimensions[dim].score
      })) : [])
    }));
  }

  // Helper method to get score color based on value (matching common-performance-table)
  // Expects score 0-100
  getScoreColor(score: number): string {
    if (score >= 80) return '#4caf50'; // Green
    if (score >= 60) return '#ff9800'; // Orange
    if (score >= 40) return '#ff5722'; // Red-Orange
    return '#f44336'; // Red
  }

  // Helper for composite score (0-10 scale)
  getScoreColorForComposite(score: number): string {
    // Convert 0-10 scale to 0-100 for color calculation
    return this.getScoreColor(score * 10);
  }

  // Get your brand name from filter data
  getYourBrandName(): string | null {
    const filterData = this.genericTemplateCommonService.getCurrentFilterData();
    if (filterData && filterData.selectedEntities && filterData.selectedEntities.length > 0) {
      return filterData.selectedEntities[0]?.name || null;
    }
    return null;
  }

  // Get audit result for your brand only (exclude competitors)
  getYourBrandAuditResult(): any | null {
    if (!this.auditResult) return null;

    // Handle structured_data format (from bulk search)
    const actualData = this.auditResult.structured_data || this.auditResult;
    
    if (!actualData.brands || actualData.brands.length === 0) {
      return null;
    }

    const yourBrandName = this.getYourBrandName();
    if (!yourBrandName) {
      // If we can't identify your brand, return the first brand as fallback
      return actualData.brands[0];
    }

    // Find the brand that matches your brand name
    const yourBrand = actualData.brands.find((brand: any) => 
      brand.brand === yourBrandName || brand.brand?.toLowerCase() === yourBrandName.toLowerCase()
    );

    return yourBrand || actualData.brands[0] || null;
  }

  /**
   * Get questions table data grouped by dimension
   */
  getQuestionsByDimension(dimension: string): Array<{
    question: string;
    pass: number;
    recommendation: string | null;
  }> {
    return this.questionsData
      .filter(q => q.dimension === dimension)
      .map(q => ({
        question: q.question,
        pass: q.pass,
        recommendation: q.recommendation
      }));
  }

  /**
   * Get all unique dimensions from questions
   */
  getDimensions(): string[] {
    return Array.from(new Set(this.questionsData.map(q => q.dimension)));
  }

  /**
   * Get only failed questions (pass === 0)
   */
  getFailedQuestions(): Array<{
    dimension: string;
    question: string;
    pass: number;
    recommendation: string | null;
  }> {
    return this.questionsData.filter(q => q.pass === 0);
  }
  
  /**
   * Re-run EEAT audit using task-based API with status polling
   */
  reAudit(): void {
    // Get project and filter data
    const projectData = this.genericTemplateCommonService.getCurrentProjectData();
    const filterData = this.genericTemplateCommonService.getCurrentFilterData();
    const projectId = this.projectId || projectData?.id;
    
    // Get projectdata_id from URL or service if available
    const urlParams = new URLSearchParams(window.location.search);
    const projectdataId = urlParams.get('projectdataId') || undefined;
    
    // EEAT is primary-brand only: do not send competitors from selectedEntities (index 0 = your brand).
    const primaryBrand = filterData?.selectedEntities?.[0]?.name?.trim();
    const country = filterData?.country || 'Global';
    const category = filterData?.category || 'General';
    
    const filters: any = {
      country: country,
      category: category,
      industry: 'General'
    };
    
    if (primaryBrand) {
      filters.brands = [primaryBrand];
    }
    
    // Start task-based audit
    this.startTaskBasedAudit({
      project_id: projectId,
      projectdata_id: projectdataId,
      filters: filters
    });
    
    // Show snackbar notification
    this.snackBar.open(
      'EEAT audit started. Progress will be shown below.',
      'Close',
      { duration: 5000 }
    );
  }

  private transformSourcesData(sources: any[]): any[] {
    return sources.map((source, index) => ({
      title: source.title || source.snippet || `Source ${index + 1}`,
      url: source.url,
      confidence: source.confidence || 0.7,
      snippet: source.snippet || source.title,
      source: source.source || "Tavily",
      category: 'eeat_audit',
      id: `source_${index}`
    }));
  }

  getEvidenceSources(): any[] {
    return this.evidenceSources || [];
  }

  /**
   * Load audit data from history without page refresh
   */
  /**
   * Load audit data from history API (only called on page load, not after audit completion)
   * After audit completion, data comes directly from status.result
   */
  private loadAuditDataFromHistory(projectdataId: string): void {
    this.loading = true; // Show loading while fetching from history
    this.projectHistoryService.getVersionData(projectdataId).subscribe({
      next: (versionData) => {
        // Update only eeat-audit data
        if (versionData.data && versionData.data['eeat-audit']) {
          const auditData = versionData.data['eeat-audit'];
          let extractedData: any = null;
          
          // Extract structured_data if available
          if (auditData.structured_data) {
            extractedData = auditData.structured_data;
          } else if (auditData.response && typeof auditData.response === 'string' && auditData.response.trim().startsWith('{')) {
            try {
              extractedData = JSON.parse(auditData.response);
            } catch (e) {
              console.warn('Failed to parse audit response JSON:', e);
            }
          } else {
            extractedData = auditData;
          }
          
          if (extractedData) {
            this.auditResult = extractedData;
            this.processEeatAuditData(extractedData);
            this.loading = false;
            this.cdr.detectChanges();
            
            // Also update the service so other components can access it
            this.genericTemplateCommonService.updateEeatAuditResponse(extractedData);
          } else {
            this.loading = false;
          }
        } else {
          this.loading = false;
        }
      },
      error: (err) => {
        console.error('Error loading audit data from history:', err);
        this.loading = false;
        // Don't show error notification on page load - just silently fail
        // User can still trigger a new audit if needed
      }
    });
  }
}
